import React from "react";
import { Link } from "react-scroll";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { HiArrowRight } from "react-icons/hi";
import heroImage from "../../assets/heropicture.jpeg";
import images from "../../constants/images";

/** The brands we build and run — shown as a continuous marquee under the fold line. */
const ventures = [
  { name: "Hyphen Tech", src: images.hyphenlogo, href: "#about" },
  { name: "AgriHyphen AI", src: images.agrihyphen, href: "https://agri.hyphentech.africa/" },
  { name: "COSEBU", src: images.cosebuLogo, href: "https://cosebu.com" },
  { name: "Hyphen Consulting", src: images.consulting, href: "#service" },
];

/**
 * Facts, not decoration — each figure is sourced from work shown further down
 * the page, so the hero makes a claim the rest of the site substantiates.
 */
const stats = [
  { value: "5",     suffix: "+",  label: "Years building" },
  { value: "50",    suffix: "+",  label: "Projects shipped" },
  { value: "94",    suffix: "%",  label: "Model accuracy" },
  { value: "110",   suffix: "+",  label: "SMEs onboarded" },
];

const ease = [0.22, 1, 0.36, 1];

const HeroSection = () => {
  const { t } = useTranslation();

  return (
    <section id="home" className="relative w-full bg-paper overflow-hidden">
      <div className="shell pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">

          {/* ── Statement ── */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3">
              <span aria-hidden className="w-8 h-px bg-gold" />
              <span className="label">{t("Digital Transformation Hub")}</span>
            </div>

            <h1 className="display-hero mt-8">
              {t("Empowering success")}
              <br />
              {t("through")} <em>{t("technology")}</em>
              <span className="text-gold">.</span>
            </h1>

            <p className="lede mt-8">
              {t(
                "We architect high-performance AI ecosystems and software solutions that define the next era of African industrial innovation."
              )}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-10">
              <Link
                to="contact"
                smooth
                duration={500}
                className="btn btn-solid cursor-pointer"
              >
                {t("Start a Project")}
                <HiArrowRight aria-hidden className="text-base" />
              </Link>
              <Link
                to="work"
                smooth
                duration={500}
                className="btn btn-outline cursor-pointer"
              >
                {t("Explore Portfolio")}
              </Link>
            </div>
          </motion.div>

          {/* ── Evidence ── */}
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.12, ease }}
            className="lg:col-span-5 group"
          >
            <div className="relative overflow-hidden bg-paper-tint aspect-[4/5]">
              {/* Near-square source in a 4:5 frame: the default centre crop keeps
                  both foreground subjects and the full height of the room. */}
              <img
                src={heroImage}
                alt={t(
                  "Hyphen Tech engineers working on laptops during a build session"
                )}
                width="1080"
                height="950"
                className="img-editorial"
              />
              <span
                aria-hidden
                className="absolute left-0 bottom-0 w-14 h-1 bg-gold"
              />
            </div>
            <figcaption className="flex gap-4 justify-between">
              <span>{t("Build session, Hyphen Tech")}</span>
              <span className="tabular shrink-0">2025</span>
            </figcaption>
          </motion.figure>
        </div>

        {/* ── Figures ── */}
        <motion.dl
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="grid grid-cols-2 md:grid-cols-4 mt-16 md:mt-24 border-t border-[color:var(--rule)]"
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`py-6 md:py-8 border-b border-[color:var(--rule)] md:border-b-0 ${
                i % 2 === 1 ? "pl-6 border-l" : ""
              } md:border-l md:first:border-l-0 md:px-8 md:first:pl-0`}
            >
              <dt className="label mb-3">{t(s.label)}</dt>
              {/* The suffix carries meaning, so it stays in ink — gold is
                  reserved for marks that lose nothing at low contrast. */}
              <dd className="display-2 tabular">
                {s.value}
                {s.suffix}
              </dd>
            </div>
          ))}
        </motion.dl>
      </div>

      {/* ── Ventures marquee ──
          Full-bleed so the logos run off both edges: it reads as an ongoing
          roster rather than a boxed-in logo grid. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="border-y border-[color:var(--rule)] bg-paper-warm"
      >
        <div className="shell py-3">
          <span className="label">{t("What we build")}</span>
        </div>

        <div className="marquee border-t border-[color:var(--rule)]">
          <div className="marquee-track">
            {[0, 1].map((copy) => (
              <ul
                key={copy}
                className="marquee-group"
                aria-hidden={copy === 1 ? "true" : undefined}
              >
                {ventures.map((venture) => (
                  <li key={`${copy}-${venture.name}`}>
                    <a
                      href={venture.href}
                      target={venture.href.startsWith("http") ? "_blank" : undefined}
                      rel={venture.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="group flex items-center gap-4 px-8 md:px-12 py-7 border-r border-[color:var(--rule)]"
                    >
                      <img
                        src={venture.src}
                        alt=""
                        aria-hidden
                        loading="lazy"
                        className="h-8 md:h-9 w-auto max-w-[7rem] object-contain grayscale opacity-55 transition-all duration-300 group-hover:grayscale-0 group-hover:opacity-100"
                      />
                      <span className="label whitespace-nowrap transition-colors duration-300 group-hover:text-ink">
                        {venture.name}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
